import { Button, Col, Input, Row, Select, Table, Tag, Tooltip } from "antd";
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getPreview,
  selectedRow,
} from "../../actionCreator/importActionCreator";
import addedToLead from "../../assets/svg/added-to-lead.svg";
import { useAlert } from "../../context/AlertProvider";
import BulkAddToLead from "../company-summary/BulkAddToLead";
import CustomPagination from "../../common/CustomPagination";
import { PAGE_LENGTH } from "../../config";
import csvImport from "../../assets/svg/csv-import.svg";
import gmail from "../../assets/images/auth/gmail.svg";
import microsoft from "../../assets/images/auth/microsoft.png";
import beyonData from "../../assets/svg/logos/logo-small.svg";
import linkedin from "../../assets/images/integration/Linkedin.svg";
const { Search } = Input;

const PreviewData = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { showAlert } = useAlert();
  const [isAddToLead, setAddToLead] = useState(false);
  const loading = useSelector((state) => state.importReducer.loading);
  const previewLeads = useSelector((state) => state.importReducer.previewLeads);
  const selectedRecords = useSelector(
    (state) => state.importReducer.selectedRecords,
  );

  useEffect(() => {
    const allParams = {};
    for (const [key, value] of searchParams.entries()) {
      allParams[key] = value;
    }

    dispatch(
      getPreview({
        ...allParams,
        page: allParams?.page ? allParams?.page - 1 : 0,
      }),
    );
  }, [dispatch, searchParams]);

  const handleConvertToLead = async () => {
    if (selectedRecords?.length) {
      setAddToLead(true);
    } else {
      showAlert("warning", "please select record.");
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "fullname",
      key: "fullname",
      render: (text, row) => (
        <strong>
          {row?.fullname ? text : row?.firstname + " " + row?.lastname}
          {row?.alreadyClientLead && (
            <Tooltip title="Added to lead">
              <img src={addedToLead} alt="" className="cursor-pointer pl-2" />
            </Tooltip>
          )}
        </strong>
      ),
    },
    {
      title: "Email",
      dataIndex: "emailId",
      key: "emailId",
    },
    {
      title: "Company",
      dataIndex: "companyName",
      key: "companyName",
    },
    {
      title: "Job Title",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Source",
      dataIndex: "leadSource",
      key: "leadSource",
      render: (source) => (
        <Tag className="tw-capitalize tw-rounded-full tw-bg-secondary-color tw-border-secondary-color tw-text-sm tw-py-1 tw-px-2">
          {source}
        </Tag>
      ),
    },
  ];

  const onFilterChange = (key, val) => {
    if (val !== undefined && !["all"].includes(val)) {
      searchParams.set(key, val);
    } else {
      searchParams.delete(key);
    }
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  const rowSelection = {
    onSelect: (record, selected, selectedRows) => {
      if (!selected) {
        const filteredRecords = selectedRecords?.filter(
          (d) => d.id !== record.key,
        );
        dispatch(selectedRow(filteredRecords));
      } else {
        dispatch(selectedRow([...selectedRecords, record]));
      }
    },
    onSelectAll: (selected, selectedRows, changeRows) => {
      const keys = changeRows?.map((d) => d.key);
      if (!selected) {
        const filteredRecords = selectedRecords?.filter(
          (d) => !keys.includes(d.key),
        );
        dispatch(selectedRow(filteredRecords));
      } else {
        dispatch(selectedRow([...selectedRecords, ...changeRows]));
      }
    },
    selectedRowKeys: selectedRecords?.map((r) => r.key),
  };

  const currentPage = parseInt(searchParams.get("page")) || 1;
  const itemsPerPage = parseInt(searchParams.get("size")) || 10;
  const totalItems = previewLeads?.totalCount;
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className="tw-mt-4">
      <BulkAddToLead
        isImportLead
        selectedItems={selectedRecords?.map((d) => ({
          ...d,
          isLead: d?.alreadyClientLead,
        }))}
        isOpen={isAddToLead}
        handleCancel={() => setAddToLead(false)}
        callback={() => {
          dispatch(selectedRow([]));
        }}
      />
      <div>
        <Row gutter={[16, 16]} className="tw-justify-between tw-mb-3">
          <Col
            xs={24}
            md={24}
            lg={15}
            className="tw-flex tw-items-center tw-gap-3"
          >
            <Search
              placeholder="Search by email, name, company or job title..."
              allowClear
              className="calendar-event-search tw-w-[43%]"
              prefix={<SearchOutlined />}
              defaultValue={searchParams.get("searchText")}
              onInput={(val) => {
                onFilterChange(
                  "searchText",
                  val.target.value ? val.target.value : undefined,
                );
                onFilterChange("page", 1);
              }}
              onClear={(val) => {
                onFilterChange("searchText", val);
                onFilterChange("page", 1);
              }}
            />
            <div className="tw-flex tw-items-center tw-justify-end tw-mb-2">
              <h6 className="m-0 text-primary font-weight-bold mob-t px-10">
                {!!selectedRecords?.length && (
                  <span className="tw-text-sm">
                    {selectedRecords?.length} records selected;{" "}
                  </span>
                )}
                {previewLeads?.totalCount > 0 && (
                  <>
                    Showing {startItem}-{endItem} <span>of</span>{" "}
                    {previewLeads?.totalCount} <span>results</span>
                  </>
                )}
              </h6>
            </div>
          </Col>
          <Col xs={24} md={12} lg={3}>
            <Select
              style={{ width: "100%" }}
              placeholder="Source"
              className="tw-custom-select"
              value={searchParams.get("sourceType") || "all"}
              options={[
                { label: "All Sources", value: "all" },
                {
                  label: (
                    <div className="tw-flex tw-items-center tw-gap-3">
                      <img src={csvImport} alt="" className="tw-size-[15px]" />{" "}
                      CSV
                    </div>
                  ),
                  value: "csv",
                },
                {
                  label: (
                    <div className="tw-flex tw-items-center tw-gap-3">
                      <img src={gmail} alt="" className="tw-size-[15px]" />{" "}
                      Google
                    </div>
                  ),
                  value: "google",
                },
                {
                  label: (
                    <div className="tw-flex tw-items-center tw-gap-3">
                      <img src={beyonData} alt="" className="tw-size-[15px]" />{" "}
                      Beyondleadz
                    </div>
                  ),
                  value: "beyondleadz",
                },
                {
                  label: (
                    <div className="tw-flex tw-items-center tw-gap-3">
                      <img src={microsoft} alt="" className="tw-size-[15px]" />{" "}
                      Microsoft
                    </div>
                  ),
                  value: "microsoft",
                },
              ]}
              onChange={(val) => onFilterChange("sourceType", val)}
            />
          </Col>
        </Row>
      </div>
      <Table
        rowSelection={{
          type: "checkbox",
          ...rowSelection,
        }}
        scroll={{ x: "max-content" }}
        dataSource={previewLeads?.content?.map((d) => ({ ...d, key: d?.id }))}
        loading={loading}
        columns={columns}
        className="tw-custom-table tw-import-preview-table"
        pagination={false}
      />
      <CustomPagination
        total={previewLeads?.totalCount}
        current={searchParams.get("page") || 1}
        pageSize={
          parseInt(searchParams.get("size"))
            ? parseInt(searchParams.get("size"))
            : PAGE_LENGTH
        }
        onChange={(page, pageSize) => {
          searchParams.set("page", page);
          searchParams.set("size", pageSize);
          navigate(`${location.pathname}?${searchParams.toString()}`);
        }}
      />
      <div className="tw-flex tw-justify-center tw-gap-8 mt30">
        <Button
          className="tw-flex tw-items-center tw-gap-3"
          type="text"
          onClick={() => {
            navigate(location.pathname);
          }}
        >
          <ArrowLeftOutlined className="tw-font-bold" /> Back to sources
        </Button>

        <Button
          className="!tw-bg-[#EAE6FF] tw-border !tw-border-theme tw-border-solid tw-rounded-[10px] !tw-text-theme tw-text-xs tw-font-semibold"
          type="primary"
          onClick={handleConvertToLead}
        >
          Convert to leads <ArrowRightOutlined className="tw-font-bold" />
        </Button>
      </div>
    </div>
  );
};

export default PreviewData;
